/**
 * ProgramSwitcher — the active-program control, rendered under the org identity in the
 * sidebar (TRUS-548).
 *
 * Renders NOTHING unless the org holds `program_switcher` and has two or more active
 * programs, so every other tenant's sidebar is byte-for-byte what it was.
 *
 * A native <select> on purpose: a custom dropdown inside the sidebar's scroll container
 * is how you end up debugging pointer-events, and this control has no styling needs a
 * native one cannot meet.
 */

import React from 'react';
import { Briefcase } from 'lucide-react';
import { useProgram } from '@/contexts/ProgramContext';

const ALL = '__all__';

export function ProgramSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { programs, selectedProgram, enabled, setSelectedProgram } = useProgram();

  if (!enabled) return null;

  if (collapsed) {
    // The sidebar is 60px wide here; show that a program is pinned without the label.
    return (
      <div
        className="flex items-center justify-center py-1 text-ink-400"
        title={selectedProgram ? `Program: ${selectedProgram.name}` : 'All programs'}
      >
        <Briefcase size={14} className={selectedProgram ? 'text-v2accent' : undefined} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 rounded-[var(--v2-r)] border border-v2border bg-surface px-2 py-1">
      <Briefcase size={12} className="flex-shrink-0 text-ink-400" />
      <label className="sr-only" htmlFor="program-switcher">
        Active program
      </label>
      <select
        id="program-switcher"
        className="min-w-0 flex-1 cursor-pointer truncate bg-transparent text-[12px] text-ink outline-none"
        value={selectedProgram ? String(selectedProgram.id) : ALL}
        title={selectedProgram ? selectedProgram.name : 'All programs'}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === ALL) {
            setSelectedProgram(null);
            return;
          }
          const next = programs.find((p) => String(p.id) === raw) || null;
          setSelectedProgram(next);
        }}
      >
        <option value={ALL}>All programs</option>
        {programs.map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export default ProgramSwitcher;
